import React from "react";
import type { Path } from "react-hook-form";
import { UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RequiredLabel,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import type { ComboboxOption } from "@/components/ui/combobox";
import { Combobox } from "@/components/ui/combobox";
import { NumericInput } from "@/components/ui/numeric-input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/context/i18n";
import { useInsuranceCompanies } from "@/pages/insurance-companies/insurance-companies.hooks";

import { TProductFormData } from "../../products.schema";
import { EProductType } from "../../products.type";
import type { TCreditProductFormData } from "../../schemas/credit-product.schema";
import { PaymentGatewayField } from "../payment-gateway-field";
import { ProductJourneyConfigTab } from "../product-journey-config-tab";
import { SoulhubExternalProductIdField } from "../soulhub-external-product-id-field";

type CreditPath = Path<TCreditProductFormData>;

const TAB_ORDER = [
  "data",
  "financial",
  "remuneration",
  "integration",
  "journey",
] as const;
type CreditTab = (typeof TAB_ORDER)[number];

export interface ICreditProductFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TProductFormData>;
  onSubmit: (values: TProductFormData) => void;
  isEditMode?: boolean;
  productId?: string | null;
}

const CREDIT_COMPANY_NONE = "__credit_no_company__";

export const CreditProductForm: React.FC<ICreditProductFormProps> = ({
  open,
  form,
  onSubmit,
  isEditMode,
  productId,
}) => {
  const { t } = useI18n();
  const { insuranceCompanies } = useInsuranceCompanies();
  const [activeTab, setActiveTab] = React.useState<CreditTab>("data");

  const creditForm = form as unknown as UseFormReturn<TCreditProductFormData>;

  const modality = creditForm.watch("credit_product_config.modality");
  const consignadoScope = creditForm.watch(
    "credit_product_config.consignado_scope",
  );
  const hasValuePremise = creditForm.watch(
    "credit_product_config.has_value_premise",
  );
  const hasAgeRestriction = creditForm.watch(
    "credit_product_config.has_age_restriction",
  );
  const insuranceCompanyId = creditForm.watch("insurance_company_id");

  React.useEffect(() => {
    if (open) {
      setActiveTab("data");
    }
  }, [open]);

  const modalityOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: "consignado", label: t("products.credit.modality.consignado") },
      { value: "pessoal", label: t("products.credit.modality.pessoal") },
      { value: "flexpay", label: t("products.credit.modality.flexpay") },
      {
        value: "fgts_antecipacao",
        label: t("products.credit.modality.fgts_antecipacao"),
      },
      {
        value: "private_label",
        label: t("products.credit.modality.private_label"),
      },
      {
        value: "home_equity",
        label: t("products.credit.modality.home_equity"),
      },
      {
        value: "auto_equity",
        label: t("products.credit.modality.auto_equity"),
      },
      {
        value: "financiamento_veiculo",
        label: t("products.credit.modality.financiamento_veiculo"),
      },
      { value: "agricola", label: t("products.credit.modality.agricola") },
    ],
    [t],
  );

  const consignadoScopeOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: "publico", label: t("products.credit.consignado.publico") },
      { value: "privado", label: t("products.credit.consignado.privado") },
    ],
    [t],
  );

  const interestBasisOptions = React.useMemo<ComboboxOption[]>(
    () => [
      {
        value: "with_insurance",
        label: t("products.credit.interest.with_insurance"),
      },
      {
        value: "without_insurance",
        label: t("products.credit.interest.without_insurance"),
      },
    ],
    [t],
  );

  const daysBasisOptions = React.useMemo<ComboboxOption[]>(
    () => [
      {
        value: "business_days",
        label: t("products.credit.days.business"),
      },
      {
        value: "calendar_days",
        label: t("products.credit.days.calendar"),
      },
      {
        value: "within_1h",
        label: t("products.credit.days.within_1h"),
      },
      {
        value: "immediate",
        label: t("products.credit.days.immediate"),
      },
    ],
    [t],
  );

  const remunerationOptions = React.useMemo<ComboboxOption[]>(
    () => [
      {
        value: "percent_on_released",
        label: t("products.credit.remuneration.percent_on_released"),
      },
      {
        value: "fixed_per_released",
        label: t("products.credit.remuneration.fixed_per_released"),
      },
      {
        value: "fixed_per_lead",
        label: t("products.credit.remuneration.fixed_per_lead"),
      },
    ],
    [t],
  );

  const targetPersonOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: "individual", label: t("products.credit.target.individual") },
      { value: "company", label: t("products.credit.target.company") },
    ],
    [t],
  );

  const fieldsForTab = React.useCallback(
    (tab: CreditTab): CreditPath[] => {
      switch (tab) {
        case "data": {
          const base: CreditPath[] = [
            "name",
            "credit_product_config.modality",
          ];
          if (modality === "consignado") {
            base.push("credit_product_config.consignado_scope");
          }
          if (modality === "consignado" && consignadoScope === "publico") {
            base.push("credit_product_config.public_sector_category");
          }
          return base;
        }
        case "financial": {
          const f: CreditPath[] = [
            "credit_product_config.interest_rate_basis",
            "credit_product_config.interest_rate_percent",
            "credit_product_config.has_value_premise",
          ];
          if (hasValuePremise) {
            f.push(
              "credit_product_config.min_released_value",
              "credit_product_config.max_released_value",
            );
          }
          return f;
        }
        case "remuneration":
          return [
            "credit_product_config.release_deadline_basis",
            "credit_product_config.release_deadline_days",
            "credit_product_config.remuneration_type",
            "credit_product_config.remuneration_value",
            "credit_product_config.allows_negative_credit",
          ];
        case "integration": {
          const i: CreditPath[] = [
            "credit_product_config.has_age_restriction",
            "credit_product_config.target_person_type",
          ];
          if (hasAgeRestriction) {
            i.push(
              "credit_product_config.min_age_years",
              "credit_product_config.max_age_years",
            );
          }
          if (insuranceCompanyId) {
            i.push("insurance_company_id");
          }
          return i;
        }
        case "journey":
          return [];
        default:
          return [];
      }
    },
    [
      modality,
      consignadoScope,
      hasValuePremise,
      hasAgeRestriction,
      insuranceCompanyId,
    ],
  );

  const validateTab = React.useCallback(
    async (tab: CreditTab): Promise<boolean> => {
      const fields = fieldsForTab(tab);
      if (fields.length === 0) return true;
      return creditForm.trigger(fields);
    },
    [creditForm, fieldsForTab],
  );

  const handleTabChange = React.useCallback(
    async (value: string) => {
      const next = value as CreditTab;
      const currentIndex = TAB_ORDER.indexOf(activeTab);
      const newIndex = TAB_ORDER.indexOf(next);

      if (newIndex < currentIndex) {
        setActiveTab(next);
        return;
      }

      const isValid = await validateTab(activeTab);
      if (isValid) {
        setActiveTab(next);
      } else {
        await creditForm.trigger(fieldsForTab(activeTab));
      }
    },
    [activeTab, validateTab, creditForm, fieldsForTab],
  );

  const gridClass =
    "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4";

  if (!open) {
    return null;
  }

  return (
    <Form {...form}>
      <form onSubmit={creditForm.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="data">
              1. {t("products.title.tab.data")}
            </TabsTrigger>
            <TabsTrigger value="financial">
              2. {t("products.credit.tab.financial")}
            </TabsTrigger>
            <TabsTrigger value="remuneration">
              3. {t("products.credit.tab.remuneration")}
            </TabsTrigger>
            <TabsTrigger value="integration">
              4. {t("products.credit.tab.rules_integration")}
            </TabsTrigger>
            <TabsTrigger value="journey">
              5. {t("products.journey.tabShort")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="data">
            <Card className="p-4">
              <div className={gridClass}>
                <FormField
                  control={creditForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="credit-name" required>
                        {t("products.form.fields.name")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="credit-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SoulhubExternalProductIdField form={form} />

                <FormField
                  control={creditForm.control}
                  name="insurance_company_id"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel htmlFor="credit-insurance-company">
                        {t("products.form.fields.insurance_company_id")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={(v) =>
                            field.onChange(
                              v === CREDIT_COMPANY_NONE ? undefined : v,
                            )
                          }
                          value={field.value || CREDIT_COMPANY_NONE}
                        >
                          <SelectTrigger
                            id="credit-insurance-company"
                            className="w-[100%]"
                          >
                            <SelectValue
                              placeholder={t(
                                "products.form.fields.insurance_company_id.placeholder",
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value={CREDIT_COMPANY_NONE}>
                                {t("generic.selectAnOption")}
                              </SelectItem>
                              {insuranceCompanies
                                .filter((c) => !!c.id)
                                .map((company) => (
                                  <SelectItem
                                    key={company.id}
                                    value={company.id || ""}
                                  >
                                    {company.name} — {company.cnpj}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <PaymentGatewayField control={creditForm.control} />

                <FormField
                  control={creditForm.control}
                  name={"credit_product_config.modality"}
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <RequiredLabel required>
                        {t("products.credit.fields.modality")}
                      </RequiredLabel>
                      <FormControl>
                        <Combobox
                          placeholder={t(
                            "products.form.fields.product_type.placeholder",
                          )}
                          options={modalityOptions}
                          value={field.value}
                          onChange={(v) => {
                            field.onChange(v);
                            if (v !== "consignado") {
                              creditForm.setValue(
                                "credit_product_config.consignado_scope",
                                undefined,
                              );
                              creditForm.setValue(
                                "credit_product_config.public_sector_category",
                                "",
                              );
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {modality === "consignado" && (
                  <FormField
                    control={creditForm.control}
                    name={"credit_product_config.consignado_scope"}
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel required>
                          {t("products.credit.fields.consignado_scope")}
                        </RequiredLabel>
                        <FormControl>
                          <Combobox
                            placeholder={t(
                              "products.form.fields.product_type.placeholder",
                            )}
                            options={consignadoScopeOptions}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {modality === "consignado" && consignadoScope === "publico" && (
                  <FormField
                    control={creditForm.control}
                    name="credit_product_config.public_sector_category"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2 lg:col-span-4">
                        <RequiredLabel htmlFor="credit-public-cat" required>
                          {t("products.credit.fields.public_sector_category")}
                        </RequiredLabel>
                        <FormControl>
                          <Input {...field} id="credit-public-cat" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="financial">
            <Card className="p-4">
              <div className={gridClass}>
                <FormField
                  control={creditForm.control}
                  name={"credit_product_config.interest_rate_basis"}
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <RequiredLabel required>
                        {t("products.credit.fields.interest_rate_basis")}
                      </RequiredLabel>
                      <FormControl>
                        <Combobox
                          placeholder={t(
                            "products.form.fields.product_type.placeholder",
                          )}
                          options={interestBasisOptions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name="credit_product_config.interest_rate_percent"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {t("products.credit.fields.interest_rate_percent")}
                      </RequiredLabel>
                      <FormControl>
                        <NumericInput
                          value={field.value ?? 0}
                          onChange={field.onChange}
                          placeholder="0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name={"credit_product_config.has_value_premise"}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:col-span-2 lg:col-span-4">
                      <FormLabel>
                        {t("products.credit.fields.has_value_premise")}
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {hasValuePremise && (
                  <>
                    <FormField
                      control={creditForm.control}
                      name="credit_product_config.min_released_value"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.credit.fields.min_released_value")}
                          </RequiredLabel>
                          <FormControl>
                            <NumericInput
                              value={field.value ?? 0}
                              onChange={field.onChange}
                              placeholder="0"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={creditForm.control}
                      name="credit_product_config.max_released_value"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.credit.fields.max_released_value")}
                          </RequiredLabel>
                          <FormControl>
                            <NumericInput
                              value={field.value ?? 0}
                              onChange={field.onChange}
                              placeholder="0"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="remuneration">
            <Card className="p-4">
              <div className={gridClass}>
                <FormField
                  control={creditForm.control}
                  name="credit_product_config.release_deadline_basis"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <RequiredLabel required>
                        {t("products.credit.fields.release_deadline_basis")}
                      </RequiredLabel>
                      <FormControl>
                        <Combobox
                          placeholder={t(
                            "products.form.fields.product_type.placeholder",
                          )}
                          options={daysBasisOptions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name="credit_product_config.release_deadline_days"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {t("products.credit.fields.release_deadline_days")}
                      </RequiredLabel>
                      <FormControl>
                        <NumericInput
                          variant="integer"
                          value={field.value ?? 0}
                          onChange={field.onChange}
                          placeholder="0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name={"credit_product_config.remuneration_type"}
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <RequiredLabel required>
                        {t("products.credit.fields.remuneration_type")}
                      </RequiredLabel>
                      <FormControl>
                        <Combobox
                          placeholder={t(
                            "products.form.fields.product_type.placeholder",
                          )}
                          options={remunerationOptions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name="credit_product_config.remuneration_value"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {t("products.credit.fields.remuneration_value")}
                      </RequiredLabel>
                      <FormControl>
                        <NumericInput
                          value={field.value ?? 0}
                          onChange={field.onChange}
                          placeholder="0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name="credit_product_config.allows_negative_credit"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:col-span-2 lg:col-span-4">
                      <FormLabel>
                        {t("products.credit.fields.allows_negative_credit")}
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

              </div>
            </Card>
          </TabsContent>

          <TabsContent value="integration">
            <Card className="p-4">
              <div className={gridClass}>
                <FormField
                  control={creditForm.control}
                  name="credit_product_config.has_age_restriction"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:col-span-2 lg:col-span-4">
                      <FormLabel>
                        {t("products.credit.fields.has_age_restriction")}
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {hasAgeRestriction && (
                  <>
                    <FormField
                      control={creditForm.control}
                      name={"credit_product_config.min_age_years"}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.credit.fields.min_age_years")}
                          </RequiredLabel>
                          <FormControl>
                            <NumericInput
                              variant="integer"
                              value={field.value ?? 0}
                              onChange={field.onChange}
                              placeholder="0"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={creditForm.control}
                      name={"credit_product_config.max_age_years"}
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel required>
                            {t("products.credit.fields.max_age_years")}
                          </RequiredLabel>
                          <FormControl>
                            <NumericInput
                              variant="integer"
                              value={field.value ?? 0}
                              onChange={field.onChange}
                              placeholder="0"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                <FormField
                  control={creditForm.control}
                  name="credit_product_config.target_person_type"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2 lg:col-span-4">
                      <RequiredLabel required>
                        {t("products.credit.fields.target_person_type")}
                      </RequiredLabel>
                      <FormControl>
                        <Combobox
                          placeholder={t(
                            "products.form.fields.product_type.placeholder",
                          )}
                          options={targetPersonOptions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={creditForm.control}
                  name={"credit_product_config.notes"}
                  render={({ field }) => (
                    <FormItem className="md:col-span-2 lg:col-span-4">
                      <FormLabel htmlFor="credit-notes">
                        {t("products.credit.fields.notes")}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          id="credit-notes"
                          {...field}
                          value={field.value ?? ""}
                          rows={4}
                          className="resize-y"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button className="mt-8 w-full max-w-md" type="submit">
                  {isEditMode
                    ? t("products.button.update")
                    : t("products.button.create")}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="journey">
            <ProductJourneyConfigTab
              productId={productId ?? null}
              productType={EProductType.CREDIT}
              onSaveSuccess={() => setActiveTab("integration")}
            />
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
};
